import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, CheckCircle2, AlertTriangle, Info, XCircle, TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight, Check } from 'lucide-react';

/* ============================================================================
   Tabadulat Partner Portal — UI primitives
   --------------------------------------------------------------------------
   One file, one import. Restrained, enterprise-grade. No gradients, no
   decorative shadows. Elevation by 1px borders. Semantic color only.
   ============================================================================ */

/* ---------- Button --------------------------------------------------------- */
export const Button = ({
  children,
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  iconLeft: IconLeft,
  iconRight: IconRight,
  className = '',
  ...rest
}) => {
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    ghost: 'btn-ghost',
    danger: 'btn-danger',
    'danger-secondary': 'btn-danger-secondary',
  };
  const sizes = { sm: 'btn-sm', md: 'btn-md' };
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={`btn ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      {loading ? (
        <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" />
      ) : (
        IconLeft && <IconLeft className="h-4 w-4" />
      )}
      {children}
      {!loading && IconRight && <IconRight className="h-4 w-4" />}
    </button>
  );
};

/* ---------- Card ----------------------------------------------------------- */
export const Card = ({ children, className = '', pad = true, ...rest }) => (
  <div className={`card ${className}`} {...rest}>
    <div className={pad ? 'card-body' : ''}>{children}</div>
  </div>
);

export const CardHeader = ({ title, description, actions }) => (
  <div className="card-header">
    <div>
      <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

/* ---------- Badge ---------------------------------------------------------- */
const BADGE_TONES = {
  neutral: 'bg-ink-100 text-ink-700 border-ink-200',
  success: 'bg-success-50 text-success-700 border-success-100',
  warning: 'bg-warning-50 text-warning-800 border-warning-100',
  danger: 'bg-danger-50 text-danger-700 border-danger-100',
  info: 'bg-info-50 text-info-700 border-info-100',
  purple: 'bg-accent-50 text-accent-700 border-accent-100',
};

export const Badge = ({ children, tone = 'neutral', dot = false, className = '' }) => (
  <span className={`badge ${BADGE_TONES[tone]} ${className}`}>
    {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
    {children}
  </span>
);

/* Role-specific badge (used in members + audit) */
export const RoleBadge = ({ role }) => {
  const tones = { OWNER: 'purple', ADMIN: 'info', MEMBER: 'neutral' };
  const labels = { OWNER: 'Owner', ADMIN: 'Admin', MEMBER: 'Member' };
  return <Badge tone={tones[role] || 'neutral'}>{labels[role] || role}</Badge>;
};

export const StatusBadge = ({ status }) => {
  const map = {
    ACTIVE: { tone: 'success', label: 'Active' },
    INVITED: { tone: 'info', label: 'Invited' },
    SUSPENDED: { tone: 'warning', label: 'Suspended' },
    REMOVED: { tone: 'danger', label: 'Removed' },
  };
  const cfg = map[status] || { tone: 'neutral', label: status };
  return <Badge tone={cfg.tone} dot>{cfg.label}</Badge>;
};

/* ---------- PageHeader ----------------------------------------------------- */
export const PageHeader = ({ title, description, actions, breadcrumb }) => (
  <div className="mb-6">
    {breadcrumb && (
      <nav className="mb-2 text-xs text-ink-500 flex items-center gap-1.5">
        {breadcrumb.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-ink-300">/</span>}
            <span className={i === breadcrumb.length - 1 ? 'text-ink-700' : ''}>{c}</span>
          </React.Fragment>
        ))}
      </nav>
    )}
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-ink-500 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  </div>
);

/* ---------- Form fields ---------------------------------------------------- */
export const Field = ({ label, hint, error, required, children }) => (
  <div>
    {label && (
      <label className="field-label">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error ? <p className="field-error">{error}</p> : hint ? <p className="field-hint">{hint}</p> : null}
  </div>
);

export const Input = React.forwardRef(({ invalid, mono, className = '', ...rest }, ref) => (
  <input
    ref={ref}
    className={`input ${mono ? 'input-mono' : ''} ${invalid ? 'input-error' : ''} ${className}`}
    {...rest}
  />
));

export const Textarea = React.forwardRef(({ invalid, className = '', ...rest }, ref) => (
  <textarea
    ref={ref}
    className={`input min-h-[96px] resize-y ${invalid ? 'input-error' : ''} ${className}`}
    {...rest}
  />
));

export const Select = React.forwardRef(({ invalid, children, className = '', ...rest }, ref) => (
  <select
    ref={ref}
    className={`input appearance-none bg-no-repeat pr-8 ${invalid ? 'input-error' : ''} ${className}`}
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L2 4h8z'/%3E%3C/svg%3E\")",
      backgroundPosition: 'right 10px center',
    }}
    {...rest}
  >
    {children}
  </select>
));

/* ---------- Modal ---------------------------------------------------------- */
export const Modal = ({ open, onClose, title, description, children, footer, width = 'md' }) => {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className={`relative w-full ${widths[width]} bg-white rounded-xl shadow-popover flex flex-col max-h-[90vh]`}>
        <div className="px-6 pt-5 pb-4 border-b border-ink-200">
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-ink-200 bg-ink-50 rounded-b-xl flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- EmptyState ----------------------------------------------------- */
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
    {Icon && (
      <div className="h-12 w-12 rounded-lg bg-ink-100 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-ink-400" />
      </div>
    )}
    <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
    {description && <p className="mt-1 text-sm text-ink-500 max-w-sm">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/* ---------- Skeleton ------------------------------------------------------- */
export const Skeleton = ({ className = '', width, height }) => (
  <div className={`skeleton ${className}`} style={{ width, height }} />
);

export const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div className="card">
    <div className="p-4 space-y-3">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4">
          {Array.from({ length: cols }).map((_, c) => (
            <div
              key={c}
              className="skeleton h-4"
              style={{ width: `${100 / cols - 2}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Table primitives ---------------------------------------------- */
export const Table = ({ children }) => <div className="table-wrap"><table className="table">{children}</table></div>;
export const THead = ({ children }) => <thead>{children}</thead>;
export const TBody = ({ children }) => <tbody>{children}</tbody>;
export const TR = ({ children, className = '', ...rest }) => (
  <tr className={className} {...rest}>{children}</tr>
);
export const TH = ({ children, className = '', ...rest }) => (
  <th className={className} {...rest}>{children}</th>
);
export const TD = ({ children, className = '', mono = false, ...rest }) => (
  <td className={`${mono ? 'font-mono text-xs tnum text-ink-600' : ''} ${className}`} {...rest}>
    {children}
  </td>
);

/* ---------- Inline alert (error banner with recovery) --------------------- */
export const Alert = ({ tone = 'danger', title, children, action }) => {
  const tones = {
    danger: 'bg-danger-50 border-danger-100 text-danger-800',
    warning: 'bg-warning-50 border-warning-100 text-warning-800',
    info: 'bg-info-50 border-info-100 text-info-800',
    success: 'bg-success-50 border-success-100 text-success-800',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          {title && <p className="text-sm font-semibold">{title}</p>}
          {children && <div className="text-sm mt-0.5 opacity-90">{children}</div>}
        </div>
        {action}
      </div>
    </div>
  );
};

/* ---------- KPI card (dashboard metric) ----------------------------------- */
export const KPI = ({ label, value, delta, deltaTone = 'neutral', hint, icon: Icon }) => {
  const deltaTones = {
    up: 'text-success-700 bg-success-50',
    down: 'text-danger-700 bg-danger-50',
    neutral: 'text-ink-600 bg-ink-100',
  };
  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-ink-500 uppercase tracking-wider">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-ink-400" />}
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-ink-900 tnum">{value}</p>
      <div className="mt-2 flex items-center gap-2">
        {delta && (
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${deltaTones[deltaTone]}`}>
            {delta}
          </span>
        )}
        {hint && <span className="text-xs text-ink-500">{hint}</span>}
      </div>
    </div>
  );
};

/* ---------- Section divider with label ------------------------------------ */
export const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 my-4">
    <span className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{children}</span>
    <div className="flex-1 h-px bg-ink-200" />
  </div>
);

/* ---------- Toast ---------------------------------------------------------- */
const TOAST_ICONS = {
  success: <CheckCircle2 className="h-4 w-4 text-success-600" />,
  danger: <XCircle className="h-4 w-4 text-danger-600" />,
  warning: <AlertTriangle className="h-4 w-4 text-warning-600" />,
  info: <Info className="h-4 w-4 text-info-600" />,
};
const TOAST_STYLES = {
  success: 'bg-white border-success-200 text-success-800',
  danger: 'bg-white border-danger-200 text-danger-800',
  warning: 'bg-white border-warning-200 text-warning-800',
  info: 'bg-white border-info-200 text-info-800',
};

export const Toast = ({ message, type = 'success', onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-elevated text-sm max-w-sm ${TOAST_STYLES[type]}`}>
      <span className="shrink-0 mt-0.5">{TOAST_ICONS[type]}</span>
      <p className="flex-1 font-medium">{message}</p>
      <button type="button" onClick={onDismiss} className="shrink-0 text-ink-400 hover:text-ink-700 -mr-1">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ToastContainer = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
    {toasts.map((t) => (
      <Toast key={t.id} message={t.message} type={t.type} onDismiss={() => onDismiss(t.id)} />
    ))}
  </div>
);

export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((message, type = 'success') => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);
  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  return { toasts, show, dismiss };
};

/* ---------- FilterPill ----------------------------------------------------- */
export const FilterPill = ({ label, value, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-50 text-accent-700 border border-accent-200 px-2.5 py-0.5 text-xs font-medium">
    <span className="text-accent-500">{label}:</span>
    {value}
    <button type="button" onClick={onRemove} className="text-accent-400 hover:text-accent-700 ml-0.5">
      <X className="h-3 w-3" />
    </button>
  </span>
);

/* ---------- StatCard ------------------------------------------------------- */
export const StatCard = ({ label, value, delta, deltaLabel, icon: Icon, trend, action }) => {
  const trendIcon = trend === 'up'
    ? <TrendingUp className="h-3.5 w-3.5" />
    : trend === 'down'
    ? <TrendingDown className="h-3.5 w-3.5" />
    : <Minus className="h-3.5 w-3.5" />;
  const trendColor = trend === 'up' ? 'text-success-700 bg-success-50' : trend === 'down' ? 'text-danger-700 bg-danger-50' : 'text-ink-600 bg-ink-100';

  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-ink-500 uppercase tracking-wider">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-ink-300" />}
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-ink-900 tnum">{value}</p>
      <div className="mt-2 flex items-center gap-2">
        {delta !== undefined && (
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${trendColor}`}>
            {trendIcon}
            {delta}
          </span>
        )}
        {deltaLabel && <span className="text-xs text-ink-500">{deltaLabel}</span>}
        {action && <span className="ml-auto">{action}</span>}
      </div>
    </div>
  );
};

/* ---------- SidePanel ------------------------------------------------------ */
export const SidePanel = ({ open, onClose, title, children, width = 'max-w-xl' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-ink-900/30 backdrop-blur-[1px]" onClick={onClose} />
      <div className={`relative flex flex-col w-full ${width} bg-white shadow-popover h-full animate-slide-in-right`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 shrink-0">
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

/* ---------- Pagination ----------------------------------------------------- */
export const Pagination = ({ page, totalPages, onPageChange, totalCount, pageSize }) => {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-ink-200 bg-white rounded-b-lg">
      <p className="text-xs text-ink-500 tnum">
        Showing <span className="font-medium text-ink-700">{start}–{end}</span> of{' '}
        <span className="font-medium text-ink-700">{totalCount}</span>
      </p>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 rounded-md text-ink-500 hover:bg-ink-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs text-ink-600 px-2 tnum">{page} / {totalPages}</span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="p-1.5 rounded-md text-ink-500 hover:bg-ink-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/* ---------- SandboxBanner -------------------------------------------------- */
export const SandboxBanner = ({ message }) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-warning-50 border border-warning-200 rounded-lg mb-6 text-sm text-warning-800">
    <AlertTriangle className="h-4 w-4 text-warning-600 shrink-0" />
    <span>{message}</span>
  </div>
);

/* ---------- ConfirmModal --------------------------------------------------- */
export const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  loading = false,
}) => (
  <Modal open={open} onClose={onClose} title={title} width="sm"
    footer={
      <>
        <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    {description && <p className="text-sm text-ink-600">{description}</p>}
  </Modal>
);

/* ---------- DateRangeFilter ------------------------------------------------ */
const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'All time', value: 'all' },
];

export const DateRangeFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = DATE_PRESETS.find((p) => p.value === value) || { label: 'All time' };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input text-sm flex items-center gap-2 w-36 justify-between"
      >
        <span>{current.label}</span>
        <ChevronRight className={`h-3.5 w-3.5 text-ink-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 w-40 bg-white rounded-lg shadow-popover border border-ink-200 py-1 z-20">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => { onChange(p.value); setOpen(false); }}
              className={`w-full px-3 py-1.5 text-sm text-left flex items-center justify-between hover:bg-ink-50 ${value === p.value ? 'text-accent-700 font-medium' : 'text-ink-700'}`}
            >
              {p.label}
              {value === p.value && <Check className="h-3.5 w-3.5 text-accent-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- CustomerStatusBadge ------------------------------------------- */
export const CustomerStatusBadge = ({ status }) => {
  const map = {
    ACTIVE: { label: 'Active', tone: 'success' },
    PENDING_KYC_VERIFICATION: { label: 'Pending KYC', tone: 'warning' },
    PENDING_MANUAL_VERIFICATION: { label: 'Manual review', tone: 'info' },
    PENDING_INITIAL_DEPOSIT: { label: 'Pending deposit', tone: 'neutral' },
    SUSPENDED: { label: 'Suspended', tone: 'danger' },
    KYC_VERIFICATION_FAILED: { label: 'KYC Failed', tone: 'danger' },
    BLOCKED: { label: 'Blocked', tone: 'danger' },
    PENDING_PROFILE_CREATION: { label: 'Incomplete profile', tone: 'warning' },
    PENDING_PERSONAL_INFORMATION: { label: 'Incomplete profile', tone: 'warning' },
    PENDING_EMPLOYMENT_DETAILS: { label: 'Incomplete profile', tone: 'warning' },
    PENDING_INVESTMENT_PROFILE: { label: 'Incomplete profile', tone: 'warning' },
    PENDING_KYC_RESULTS: { label: 'KYC in review', tone: 'info' },
    PENDING_TIN_SUBMISSION: { label: 'TIN needed', tone: 'warning' },
  };
  const { label, tone } = map[status] || { label: status, tone: 'neutral' };
  return <Badge tone={tone} dot>{label}</Badge>;
};

/* ---------- FinancialStatusBadge ------------------------------------------ */
export const FinancialStatusBadge = ({ status }) => {
  const map = {
    CREATED: { label: 'Created', tone: 'neutral' },
    PENDING_APPROVAL: { label: 'Pending review', tone: 'warning' },
    RECEIVED: { label: 'Received', tone: 'info' },
    UNDER_PROCESSING: { label: 'Processing', tone: 'info' },
    APPROVED: { label: 'Approved', tone: 'success' },
    REJECTED: { label: 'Rejected', tone: 'danger' },
    CANCELLED: { label: 'Cancelled', tone: 'neutral' },
    EXPIRED: { label: 'Expired', tone: 'danger' },
    PENDING_PAYMENT_ACTION: { label: 'Awaiting payment', tone: 'warning' },
  };
  const { label, tone } = map[status] || { label: status, tone: 'neutral' };
  return <Badge tone={tone} dot>{label}</Badge>;
};

/* ---------- CopyButton (shared) ------------------------------------------- */
export const CopyButton = ({ value, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const handleCopy = useCallback(
    (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      });
    },
    [value],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors
        ${copied
          ? 'bg-success-50 text-success-700 border border-success-200'
          : 'bg-ink-100 text-ink-500 hover:bg-ink-200 hover:text-ink-700 border border-transparent'
        } ${className}`}
    >
      <Check className={`h-3 w-3 ${copied ? '' : 'opacity-0'}`} aria-hidden />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};
